/**
 * The firm's own name and logo, for the screens their staff look at.
 * @file src/web/shared/hooks/useFirmBrand.ts
 *
 * A firm's staff spend their day in this app. Showing them our mark at the
 * top of it, next to their own clients' names, makes it our software they
 * are using rather than their own practice — and the same logo belongs on
 * the builder's preview, because that preview is a promise about what the
 * client will see.
 *
 * Reads the profile the rest of the app has already loaded, so this costs
 * nothing extra. Null logo is the normal state until a firm uploads one.
 *
 * @llm-rule WHEN: A staff screen shows whose workspace this is
 * @llm-rule AVOID: Fetching /api/firm-settings again — it is already cached
 */
import { useEffect, useState } from 'react';
import { apiAsset } from '@/shared/api';
import { fetchFirmProfile } from './useFirmLanguages';

export interface FirmBrand {
  name: string | null;
  /** Ready for an <img src>, or null. */
  logoSrc: string | null;
  loading: boolean;
}

export function useFirmBrand(): FirmBrand {
  const [brand, setBrand] = useState<FirmBrand>({ name: null, logoSrc: null, loading: true });

  useEffect(() => {
    let live = true;
    void fetchFirmProfile().then((p) => {
      if (!live) return;
      setBrand({
        name: p.firmName,
        logoSrc: p.logoUrl ? apiAsset(p.logoUrl) : null,
        loading: false,
      });
    });
    return () => {
      live = false;
    };
  }, []);

  return brand;
}
